import { useState } from "react";
import { Carousel } from "react-responsive-carousel";
import { Link } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaStar, FaBookOpen } from "react-icons/fa";
import useGetSpotlightedBooks from "api/BookResource/useGetSpotlightedBooks";
import { getMoneyPrefix, activeCurrencies } from "utils";
import useCurrencyStore from "store/currencyStore";

const SpotlightCarousel = ({ mini = false }) => {
	const { data: spotlightedBooks, isLoading } = useGetSpotlightedBooks();
	const { currency } = useCurrencyStore();
	const [isPaused, setIsPaused] = useState(false);

	const books = spotlightedBooks?.data || [];

	if (isLoading || books.length === 0) {
		return null;
	}

	const formatPrice = (book) => {
		const price = currency === activeCurrencies.USD.code ? book?.price : book?.zwl_price;
		const prefix = getMoneyPrefix(currency);
		return price ? `${prefix}${Number(price).toFixed(2)}` : "Free";
	};

	const handleMouseEnter = () => setIsPaused(true);
	const handleMouseLeave = () => setIsPaused(false);

	const renderArrowPrev = (onClickHandler, hasPrev) => (
		<button
			type="button"
			onClick={onClickHandler}
			disabled={!hasPrev}
			aria-label="Previous spotlight book"
			className={`spotlight-arrow absolute left-4 md:left-8 top-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-500 ${
				hasPrev ? "spotlight-arrow-active" : "spotlight-arrow-disabled"
			}`}
		>
			<FaChevronLeft size={24} />
		</button>
	);

	const renderArrowNext = (onClickHandler, hasNext) => (
		<button
			type="button"
			onClick={onClickHandler}
			disabled={!hasNext}
			aria-label="Next spotlight book"
			className={`spotlight-arrow absolute right-4 md:right-8 top-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-500 ${
				hasNext ? "spotlight-arrow-active" : "spotlight-arrow-disabled"
			}`}
		>
			<FaChevronRight size={24} />
		</button>
	);

	const renderIndicator = (onClickHandler, isSelected, index, label) => (
		<button
			type="button"
			onClick={onClickHandler}
			title={label}
			aria-label={`Go to spotlight slide ${index + 1}`}
			className={`spotlight-indicator h-2.5 rounded-full transition-all duration-500 mx-1.5 ${
				isSelected ? "bg-akgreen w-10" : "bg-white bg-opacity-50 hover:bg-opacity-100 w-2.5"
			}`}
		/>
	);

	return (
		<div
			className="relative w-full overflow-hidden"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
		<Carousel
			showThumbs={false}
			showStatus={false}
			showIndicators={true}
			showArrows={true}
				infiniteLoop={true}
				autoPlay={!isPaused}
				interval={6000}
			swipeable={true}
			renderArrowPrev={renderArrowPrev}
			renderArrowNext={renderArrowNext}
			renderIndicator={renderIndicator}
			emulateTouch={true}
			className={`spotlight-carousel ${mini ? "mini-carousel" : ""}`}
		>
				{books.map((book, index) => {
					const coverUrl = book?.book_cover?.url;
					const hasCover = Boolean(coverUrl);

					return (
						<div
							key={book.id || index}
							className={`relative w-full spotlight-slide spotlight-slide-shell ${mini ? "mini-slide" : ""}`}
						>
							{/* Background Image with Overlay - Only for full mode */}
							{!mini && (
								<>
									<div
										className={`absolute inset-0 bg-akgreen spotlight-bg transition-transform ease-linear ${
											hasCover
												? "bg-cover bg-center"
												: "bg-gradient-to-br from-akgreener via-akgreen to-gray-50"
										}`}
										style={
											hasCover
												? {
														backgroundImage: `url(${coverUrl})`,
														backgroundSize: "cover",
														backgroundPosition: "center",
													}
												: {}
										}
									/>

									{/* Gradient Overlays for extra depth - using vibrant brand greens */}
									<div
										className={`absolute inset-0 ${hasCover ? "bg-gradient-cover" : "bg-gradient-empty"}`}
									/>
									<div className="absolute inset-0 radial-overlay pointer-events-none" />
								</>
							)}

							{/* Content */}
							<div
								className={`relative h-full w-full mx-auto flex items-center justify-center ${mini ? "" : "max-w-7xl px-6 sm:px-8 md:px-12 lg:px-16 lg:justify-start"}`}
							>
								<div
									className={`flex items-center justify-center w-full ${mini ? "flex-col" : "flex-col lg:flex-row lg:justify-between gap-8 lg:gap-16"}`}
								>
									{/* Glassmorphic Text Panel - Only for full mode */}
									{!mini && (
										<div
											className={`flex-1 order-1 lg:order-1 w-full z-10 mt-8 lg:mt-0 ${
												hasCover ? "text-center lg:text-left" : "text-center max-w-4xl mx-auto"
											}`}
										>
										<div
											className={`glass-panel p-6 sm:p-8 md:p-10 rounded-3xl transition-all duration-700 py-8 spotlight-panel ${hasCover ? "mx-auto lg:mx-0 max-w-2xl text-center lg:text-left" : "mx-auto w-full text-center"}`}
										>
											{/* Spotlight Label with Glow */}
											<div
												className={`inline-flex items-center gap-2 spotlight-label text-yellow-300 px-5 py-2 rounded-full text-xs sm:text-sm font-bold tracking-widest uppercase mb-6 ${hasCover ? "mx-auto lg:mx-0" : "mx-auto"}`}
												>
													<FaStar className="animate-pulse drop-shadow-star" />
													<span>Spotlight</span>
												</div>

												{/* Title */}
												<h2
													className={`text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-cera-black leading-tight mb-4 text-white drop-shadow-md`}
												>
													{book.title}
												</h2>

												{/* Author */}
												{book.author && (
													<p className="text-xl sm:text-2xl text-white opacity-90 mb-2 font-light drop-shadow-md">
														by{" "}
														<span className="font-bold text-white tracking-wide">
															{book.author}
														</span>
													</p>
												)}

												{/* Publisher */}
												{book.publisher && (
													<p className="text-xs sm:text-sm text-white opacity-50 mb-6 uppercase tracking-widest font-bold">
														Published by {book.publisher}
													</p>
												)}

												{/* Rating & Description Row */}
												<div
													className={`flex flex-col gap-5 mb-8 ${hasCover ? "items-center lg:items-start" : "items-center"}`}
												>
													{/* Rating */}
													{book.rating > 0 && (
														<div className="flex items-center gap-3 rating-pill px-5 py-2.5 rounded-full border border-white border-opacity-40">
															<div className="flex items-center gap-1.5">
																{[1, 2, 3, 4, 5].map((star) => (
																	<FaStar
																		key={star}
																		size={16}
																		className={
																			star <= Math.round(book.rating)
																				? "text-yellow-400 drop-shadow-star-small"
																				: "text-white opacity-20"
																		}
																	/>
																))}
															</div>
															<span className="text-white opacity-90 text-sm font-bold border-l border-white border-opacity-20 pl-3">
																{Number(book.rating).toFixed(1)} Rating
															</span>
														</div>
													)}

													{/* Description */}
													{book.description && (
														<p className="text-white opacity-80 text-base sm:text-lg leading-relaxed line-clamp-3 font-light drop-shadow-sm">
															{book.description}
														</p>
													)}
												</div>

												{/* Price & CTA */}
												<div
													className={`flex flex-col sm:flex-row items-center gap-6 mt-4 pt-6 border-t border-white border-opacity-10 ${hasCover ? "justify-center lg:justify-start" : "justify-center"}`}
												>
													<div className="flex items-baseline gap-2">
														{!book.isFree && (
															<span className="text-4xl sm:text-5xl font-cera-black text-white drop-shadow-xl">
																{formatPrice(book)}
															</span>
														)}
														{book.isFree && (
															<span className="text-4xl sm:text-5xl font-cera-black text-green-400 drop-shadow-xl">
																Free
															</span>
														)}
													</div>

													<Link
														to={`/book/${book.id}`}
														className="group relative flex items-center justify-center gap-3 overflow-hidden rounded-full p-0.5 transition-transform duration-300 hover:scale-105 active:scale-95 cta-shadow"
													>
														<span className="absolute inset-0 bg-gradient-to-r from-orangelib via-orange-400 to-orangelib bg-double transition-all duration-700 group-hover:bg-pos-right"></span>
														<span className="relative flex items-center gap-3 cta-inner px-8 py-4 rounded-full text-white font-bold text-lg leading-none transition-colors border border-white border-opacity-20 group-hover:bg-transparent">
															<FaBookOpen className="text-xl transition-transform duration-300 group-hover:transform group-hover:rotate-12" />
															<span>Read Now</span>
														</span>
													</Link>
												</div>
											</div>
										</div>
									)}

									{/* Book Cover Container with Hover 3D Shift */}
									{hasCover && (
										<div
											className={`flex-shrink-0 relative z-10 perspective ${mini ? "mini-cover-column mt-2" : "order-2 lg:order-2 w-48 sm:w-56 md:w-64 lg:w-80 mt-4 lg:mt-0 mb-8 lg:mb-0"}`}
										>
											<div className="relative transition-all duration-700 ease-out transform spotlight-cover-wrap">
												{/* Glow effect behind book - using a lighter green */}
												<div className="absolute inset-n5 bg-gradient-to-r from-akgreen to-yellow-300 blur-2xl opacity-40 spotlight-cover-glow transition-opacity duration-700 rounded-3xl"></div>

												<div className={mini ? "mini-cover-frame" : ""}>
													<Link to={`/book/${book.id}`}>
														<img
															src={coverUrl}
															alt={book.title}
															className={`relative w-full rounded-xl cover-shadow transform border border-white border-opacity-30 transition-all duration-500 spotlight-cover-image ${mini ? "mini-cover-image shadow-2xl" : ""}`}
														/>
													</Link>
												</div>
												{/* Spotlight Badge */}
												<div className="absolute -top-5 -right-5 badge-gradient text-black text-xs sm:text-sm font-black tracking-wider px-5 py-2 rounded-full shadow-lg transform rotate-12 spotlight-badge transition-all duration-500 z-20 border border-yellow-200">
													Featured
												</div>

												{/* Mini Info - Only for mini mode */}
												{mini && (
													<div className="mt-6 text-center space-y-2 mini-spotlight-info transition-all duration-500">
														<h3 className="text-white font-cera-black text-lg line-clamp-1 drop-shadow-md">
															{book.title}
														</h3>
														<div className="flex items-center justify-center gap-3">
															<span className="text-yellow-300 font-bold">{formatPrice(book)}</span>
															<Link
																to={`/book/${book.id}`}
																className="mini-details-link px-4 py-1.5 rounded-full text-white text-xs font-bold transition-all"
															>
																View Details
															</Link>
														</div>
													</div>
												)}
											</div>
										</div>
									)}
								</div>
							</div>

							{/* End of content */}
						</div>
					);
				})}
			</Carousel>

			{/* Custom Styles */}
			<style jsx>{`
				.spotlight-slide:not(.mini-slide) {
					height: 60vh;
					min-height: 500px;
					max-height: 700px;
				}
				.spotlight-slide-shell:hover .spotlight-bg {
					transform: scale(1.05);
				}
				.spotlight-slide-shell:hover .spotlight-panel {
					box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2);
				}
				.spotlight-slide-shell:hover .spotlight-cover-wrap {
					transform: translateY(-10px) rotateY(-6deg);
				}
				.spotlight-slide-shell:hover .spotlight-cover-glow {
					opacity: 0.6;
				}
				.spotlight-slide-shell:hover .spotlight-cover-image {
					transform: scale(1.05);
				}
				.spotlight-slide-shell:hover .spotlight-badge {
					transform: rotate(0deg);
				}
				.spotlight-slide-shell:hover .mini-spotlight-info {
					transform: translateY(0);
					opacity: 1;
				}
				.mini-slide {
					height: 500px;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.mini-cover-column {
					width: 100%;
					max-width: 320px;
				}
				.mini-cover-frame {
					width: 100%;
					height: 300px;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.mini-cover-frame a {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;
				}
				.mini-cover-image {
					width: auto;
					max-width: 100%;
					height: 100%;
					max-height: 100%;
					object-fit: contain;
				}
				.spotlight-arrow {
					transform: translateY(-50%);
				}
				.spotlight-arrow-active {
					background: rgba(255, 255, 255, 0.2);
					color: #fff;
					border: 1px solid rgba(255, 255, 255, 0.3);
					box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
					backdrop-filter: blur(14px);
					-webkit-backdrop-filter: blur(14px);
				}
				.spotlight-arrow-active:hover {
					background: rgba(255, 255, 255, 0.92);
					color: #5fa000;
					transform: translateY(-50%) scale(1.08);
				}
				.spotlight-arrow-disabled {
					background: rgba(255, 255, 255, 0.06);
					color: rgba(255, 255, 255, 0.2);
					cursor: not-allowed;
					display: none;
				}
				.spotlight-indicator {
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
				}
				:global(.mini-carousel .control-dots) {
					bottom: 0px !important;
				}
				:global(.mini-carousel .control-arrow) {
					opacity: 0.2;
				}
				:global(.mini-carousel:hover .control-arrow) {
					opacity: 1;
				}
				.duration-10000 {
					transition-duration: 10000ms;
				}
				.white-opacity-20 {
					color: rgba(255, 255, 255, 0.2);
				}
				.bg-gradient-cover {
					background: linear-gradient(
						to right,
						rgba(95, 160, 0, 0.9) 0%,
						rgba(95, 160, 0, 0.6) 60%,
						transparent 100%
					);
				}
				.bg-gradient-empty {
					background: linear-gradient(to right, rgba(95, 160, 0, 0.2) 0%, transparent 100%);
				}
				.radial-overlay {
					background: radial-gradient(
						circle at center,
						transparent 0%,
						rgba(127, 180, 45, 0.2) 100%
					);
				}
				.glass-panel {
					background: rgba(255, 255, 255, 0.2);
					backdrop-filter: blur(40px);
					-webkit-backdrop-filter: blur(40px);
					border: 1px solid rgba(255, 255, 255, 0.4);
					box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
				}
				.spotlight-label {
					background: linear-gradient(to right, rgba(250, 204, 21, 0.3), rgba(250, 204, 21, 0.1));
					border: 1px solid rgba(250, 204, 21, 0.5);
					box-shadow: 0 0 20px rgba(250, 204, 21, 0.3);
				}
				.drop-shadow-star {
					filter: drop-shadow(0 0 8px rgba(250, 204, 21, 0.8));
				}
				.drop-shadow-star-small {
					filter: drop-shadow(0 0 8px rgba(250, 204, 21, 0.6));
				}
				.rating-pill {
					background: rgba(255, 255, 255, 0.3);
					box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
				}
				.bg-double {
					background-size: 200% auto;
				}
				.bg-pos-right {
					background-position: 100% 0%;
				}
				.cta-inner {
					background: rgba(255, 255, 255, 0.1);
					backdrop-filter: blur(10px);
					-webkit-backdrop-filter: blur(10px);
				}
				.cta-shadow {
					box-shadow: 0 10px 30px rgba(236, 107, 52, 0.4);
				}
				.cta-shadow:hover {
					box-shadow: 0 15px 40px rgba(236, 107, 52, 0.6);
				}
				.perspective {
					perspective: 1200px;
				}
				.inset-n5 {
					top: -20px;
					right: -20px;
					bottom: -20px;
					left: -20px;
				}
				.blur-2xl {
					filter: blur(40px);
				}
				.cover-shadow {
					box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
				}
				.badge-gradient {
					background: linear-gradient(135deg, #facc15, #ca8a04);
				}
				.mini-spotlight-info {
					transform: translateY(16px);
					opacity: 0;
					min-height: 82px;
					padding: 0 14px;
				}
				.mini-details-link {
					background: rgba(255, 255, 255, 0.2);
					backdrop-filter: blur(14px);
					-webkit-backdrop-filter: blur(14px);
				}
				.mini-details-link:hover {
					background: rgba(255, 255, 255, 0.38);
				}
				:global(.spotlight-carousel .carousel-slider) {
					height: 100%;
					overflow: visible;
				}
				:global(.spotlight-carousel .slider-wrapper) {
					height: 100%;
					overflow: visible;
				}
				:global(.spotlight-carousel .slide) {
					height: 100%;
					background: transparent;
				}
				:global(.spotlight-carousel .control-dots) {
					position: absolute;
					bottom: 30px;
					left: 50%;
					transform: translateX(-50%);
					display: flex;
					align-items: center;
					justify-content: center;
					z-index: 30;
					margin: 0;
					padding: 0;
					width: auto;
				}
				.line-clamp-3 {
					display: -webkit-box;
					-webkit-line-clamp: 3;
					-webkit-box-orient: vertical;
					overflow: hidden;
				}
				:global(.carousel .animated) {
					transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
				}
				@media (max-width: 1023px) {
					.spotlight-slide:not(.mini-slide) {
						height: auto;
						min-height: 560px;
					}
					.spotlight-slide-shell:hover .spotlight-cover-wrap {
						transform: translateY(-6px);
					}
				}
				@media (max-width: 1279px) {
					.mini-cover-frame {
						height: 270px;
					}
				}
			`}</style>
		</div>
	);
};

export default SpotlightCarousel;
